import { 
  getFirestore, 
  collection, 
  doc,
  setDoc,
  deleteDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  getDoc,
  getDocs,
  query, 
  where, 
  orderBy,
  limit,
  serverTimestamp ,
  increment,
} from 'firebase/firestore';

import { getAuth } from 'firebase/auth';

// New function for media posts
export const createPostWithMedia = async (userId, postData) => {
  try {
    // Get authentication token
    const auth = getAuth();
    const token = await auth.currentUser.getIdToken();
    
    // Check if this is an embed post (has embedData but no mediaFiles)
    const isEmbedPost = postData.embedData && !postData.mediaFiles;
    
    // Prepare the request data
    const requestData = {
      userId,
      ...postData
    };
    
    // Log what we're sending - helpful for debugging
    console.log('Creating post with data:', {
      type: postData.type,
      hasMediaFiles: !!postData.mediaFiles,
      hasEmbedData: !!postData.embedData,
      isEmbedPost
    });
    
    // Send request to Cloud Function
    const response = await fetch('/createPostWithMedia', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestData)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create post with media');
    }
    
    const data = await response.json();
    return data.postData;
  } catch (error) {
    console.error('Error creating post with media:', error);
    throw error;
  }
};

// New function for deleting media posts
export const deleteMediaPost = async (userId, postId) => {
  try {
    // Get authentication token
    const auth = getAuth();
    const token = await auth.currentUser.getIdToken();
    
    // Send request to Cloud Function
    const response = await fetch('/api/deleteMediaPost', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        userId,
        postId
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete media post');
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting media post:', error);
    throw error;
  }
};

export const deletePost = async (userId, postId) => {
  try {
    const db = getFirestore();
    
    // Get post data to remove tags
    const postRef = doc(db, `users/${userId}/posts`, postId);
    const postDoc = await getDoc(postRef);
    const postData = postDoc.data();
    
    // Delete from both collections
    await deleteDoc(postRef);
    await deleteDoc(doc(db, 'posts', postId));

    // Get user's posts to check if tags are still in use
    const userPostsRef = collection(db, `users/${userId}/posts`);
    const postsSnapshot = await getDocs(userPostsRef);
    const remainingTags = new Set();
    postsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.tags) {
        data.tags.forEach(tag => remainingTags.add(tag));
      }
    });

    // Remove tags that are no longer used in any posts
    const tagsToRemove = postData.tags.filter(tag => !remainingTags.has(tag));
    if (tagsToRemove.length > 0) {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        postTags: arrayRemove(...tagsToRemove),
        postsCount: increment(-1)
      });
    }

    return true;
  } catch (error) {
    console.error('Error deleting post:', error);
    throw error;
  }
};
  
 

export const updatePost = async (userId, postId, updatedData) => {
  try {
    const db = getFirestore();
    const timestamp = serverTimestamp();
    
    // Prepare update data
    const updateData = {
      ...updatedData,
      updatedAt: timestamp,
    };

    // Get original post to compare tags
    const postRef = doc(db, `users/${userId}/posts`, postId);
    const postDoc = await getDoc(postRef);
    const originalData = postDoc.data();

    // Update both locations
    await updateDoc(postRef, updateData);
    await updateDoc(doc(db, 'posts', postId), updateData);

    // Update user's tags if they've changed
    if (originalData.tags?.join(',') !== updatedData.tags?.join(',')) {
      const userRef = doc(db, 'users', userId);
      
      // Get all posts to determine which tags are still in use
      const userPostsRef = collection(db, `users/${userId}/posts`);
      const postsSnapshot = await getDocs(userPostsRef);
      const currentTags = new Set();
      
      postsSnapshot.docs.forEach(doc => {
        if (doc.id !== postId) { // Exclude current post
          const data = doc.data();
          if (data.tags) {
            data.tags.forEach(tag => currentTags.add(tag));
          }
        }
      });

      // Add new tags from updated post
      updatedData.tags?.forEach(tag => currentTags.add(tag));

      // Update user's tags
      await updateDoc(userRef, {
        postTags: Array.from(currentTags)
      });
    }

    return updateData;
  } catch (error) {
    console.error('Error updating post:', error);
    throw error;
  }
};
  
export const getUserPosts = async (userId, postsLimit = 2, tagFilter = null) => {
  try {
    const db = getFirestore();
    const postsRef = collection(db, `users/${userId}/posts`);
    let q;

    if (tagFilter) {
      // Convert tagFilter to lowercase for consistency
      const normalizedTagFilter = tagFilter.toLowerCase().replace(/\s+/g, '');
      q = query(
        postsRef,
        where('tags', 'array-contains', normalizedTagFilter),
        orderBy('createdAt', 'desc'),
        limit(postsLimit)
      );
    } else {
      q = query(
        postsRef,
        orderBy('createdAt', 'desc'),
        limit(postsLimit)
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      postId: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching user posts:', error);
    throw error;
  }
};
  
export const validatePostUrl = (url, type) => {
  if (!url) return false;

  // For audio embeds
  if (type === 'audio') {
    const supportedPlatforms = [
      'spotify.com', 
      'soundcloud.com', 
      'soundcloud.app.goog.gl'
    ];
    
    return supportedPlatforms.some(platform => url.includes(platform));
  }

  // For other types (video, photo), you might want more flexible validation
  // This allows local file uploads or URLs
  return true;
};
  
  export const getUserTags = async (userId) => {
    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        return userDoc.data()?.postTags || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching user tags:', error);
      return [];
    }
  };